/**
 * Billing API client — single-plan v2.0.
 *
 * Backend routes (already live):
 *   GET    /billing/usage              → BillingUsageController          (SP-033)
 *   GET    /billing/credit-packs       → BillingCreditsController.index  (SP-023)
 *   POST   /billing/credit-packs       → BillingCreditsController.store  (SP-023)
 *   GET    /billing/subscription       → BillingManagementController.show
 *   GET    /billing/growth             → GrowthBillingStateController    (Growth paywall)
 *   POST   /billing/cancel             → BillingManagementController     (SP-040)
 *
 * Growth is the single plan and is sales-led (2026-10-20): the clinic does
 * not subscribe, upgrade or downgrade. The CliniSharp team issues both
 * charges; the clinic pays through the Asaas links in `getGrowthState`.
 *
 * Note: routes are mounted under prefix /api/v1 in routes/tenant.php and the
 * VITE_API_URL baseURL already includes /api/v1, so paths here are written
 * without the prefix (matches every other service in this folder).
 */

import { httpRequest } from "@/lib/axios";

export type Metric = "ai_conversations" | "ai_messages";
export type PackSize = "small" | "medium" | "large";
export type CreditPackStatus = "pending" | "confirmed" | "failed" | "refunded";

export interface AllowanceShape {
  plan_limit: number;
  plan_used: number;
  credits_total: number;
  credits_used: number;
  period_end: string | null;
}

export interface CreditPack {
  id: string;
  metric: Metric;
  amount: number;
  amount_used: number;
  amount_remaining: number;
  price_brl: number;
  status: CreditPackStatus;
  purchased_at: string | null;
  expires_at: string | null;
  consumed_at: string | null;
}

export interface UsageResponse {
  ai_conversations: AllowanceShape;
  ai_messages: AllowanceShape;
  scheduled_changes: {
    downgrade_to_plan_id: string | null;
    effective_at: string | null;
  };
  credit_packs: CreditPack[];
}

export interface PurchaseCreditsResponse {
  pack: CreditPack;
  payment_link: string;
}

export type BillingPeriod = "monthly" | "annual";

export interface BillingPlanSnapshot {
  id: string;
  name: string;
  /** `growth`, or a retired tier a legacy subscription still carries. */
  tier: string | null;
  billing_period: BillingPeriod | null;
  price: number;
}

export interface SubscriptionSnapshot {
  subscription_id: string;
  status: string;
  end_date: string | null;
  period_end: string | null;
  plan: BillingPlanSnapshot | null;
  scheduled_plan: BillingPlanSnapshot | null;
  scheduled_change_at: string | null;
}

export interface CancelResponse {
  subscription_id: string;
  status: string;
  end_date: string | null;
}

export type ActivationStatus = "pending" | "paid";

export interface GrowthActivation {
  id: string;
  status: ActivationStatus;
  amount_brl: number;
  /** Asaas invoice link while the charge is pending, otherwise null. */
  payment_link: string | null;
  paid_at: string | null;
}

export interface GrowthSubscription {
  subscription_id: string;
  status: string;
  /** Asaas link while the subscription is pending, otherwise null. */
  payment_link: string | null;
}

/** The clinic's two Growth charges; null means not issued yet. */
export interface GrowthBillingState {
  plan: "growth";
  activation: GrowthActivation | null;
  subscription: GrowthSubscription | null;
}

export const BillingAPI = {
  getUsage(): Promise<UsageResponse> {
    return httpRequest.get<UsageResponse>("/billing/usage").then((r) => r.data);
  },

  listCreditPacks(): Promise<CreditPack[]> {
    return httpRequest
      .get<{ data: CreditPack[] }>("/billing/credit-packs")
      .then((r) => r.data.data);
  },

  purchaseCredits(
    metric: Metric,
    size: PackSize
  ): Promise<PurchaseCreditsResponse> {
    return httpRequest
      .post<{ data: PurchaseCreditsResponse }>("/billing/credit-packs", {
        metric,
        size,
      })
      .then((r) => r.data.data);
  },

  getSubscription(): Promise<SubscriptionSnapshot | null> {
    return httpRequest
      .get<{ data: SubscriptionSnapshot | null }>("/billing/subscription")
      .then((r) => r.data.data);
  },

  getGrowthState(): Promise<GrowthBillingState> {
    return httpRequest
      .get<{ data: GrowthBillingState }>("/billing/growth")
      .then((r) => r.data.data);
  },

  cancel(reason?: string): Promise<CancelResponse> {
    return httpRequest
      .post<{ data: CancelResponse }>(
        "/billing/cancel",
        reason ? { reason } : {}
      )
      .then((r) => r.data.data);
  },
};
